import { computed, ref } from 'vue';
import {
  elementScroll,
  observeElementOffset,
  observeElementRect,
  observeWindowOffset,
  observeWindowRect,
  Rect,
  useVirtualizer,
  Virtualizer,
  VirtualizerOptions,
} from '@tanstack/vue-virtual';
import { useLayoutScrollContainer } from '@/utils/composables/useLayoutScrollContainer';

export type Props = {
  getCount: () => number;
} & Pick<VirtualizerOptions<Window, Element>, 'estimateSize' | 'overscan'>;

const observeRect = (
  instance: Virtualizer<Element, Element>,
  cb: (rect: Rect) => void,
) =>
  instance.scrollElement instanceof HTMLElement
    ? observeElementRect(instance, cb)
    : observeWindowRect(
        instance as unknown as Virtualizer<Window, Element>,
        cb,
      );
const observeOffset = (
  instance: Virtualizer<Element, Element>,
  cb: (offset: number, isScrolling: boolean) => void,
) =>
  instance.scrollElement instanceof HTMLElement
    ? observeElementOffset(instance, cb)
    : observeWindowOffset(
        instance as unknown as Virtualizer<Window, Element>,
        cb,
      );

/**
 * ページの実スクロール要素（旧レイアウトはwindow、新レイアウトはAppShellのcontent領域）基準の仮想スクロール。
 */
export const usePageVirtualScroll = ({
  getCount,
  estimateSize,
  overscan = 0,
}: Props) => {
  const scrollableElement = ref<HTMLElement | null>(null);
  const scrollMargin = ref(0);
  const { scrollContainer } = useLayoutScrollContainer();

  const rowVirtualizerOptions = computed(() => ({
    count: getCount(),
    estimateSize,
    scrollMargin: scrollMargin.value,
    overscan,
    getScrollElement: () => scrollContainer.value as Element,
    observeElementRect: observeRect,
    observeElementOffset: observeOffset,
    scrollToFn: elementScroll,
  }));
  const rowVirtualizer = useVirtualizer(rowVirtualizerOptions);
  const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems());
  const totalSize = computed(() => rowVirtualizer.value.getTotalSize());

  const getScrollMargin = (el: HTMLElement): number => {
    const rect = el.getBoundingClientRect();
    const container = scrollContainer.value;
    if (container instanceof Window) return rect.top + container.scrollY;
    return (
      rect.top - container.getBoundingClientRect().top + container.scrollTop
    );
  };

  return {
    rowVirtualizer,
    virtualRows,
    scrollMargin,
    scrollableElement,
    totalSize,
    getScrollMargin,
  };
};
